import { I18N_KEYS } from "common/i18n/keys";

export const TOTAL_DATE_CELLS = 42;

/**
 * Date a가 Date b보다 이전인지 비교 (시간 포함)
 * @param {Date} a
 * @param {Date} b
 * @returns {boolean}
 */
export const isBefore = (a, b) => a.getTime() < b.getTime();

/**
 * Date a가 Date b보다 이전인지 비교 (날짜만, 시간 무시)
 * @param {Date} a
 * @param {Date} b
 * @returns {boolean}
 */
export const isBeforeByDate = (a, b) => {
  const aDate = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const bDate = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return aDate.getTime() < bDate.getTime();
};

/**
 * Date a가 Date b보다 이후인지 비교 (시간 포함)
 * @param {Date} a
 * @param {Date} b
 * @returns {boolean}
 */
export const isAfter = (a, b) => a.getTime() > b.getTime();

/**
 * Date a가 Date b보다 이후인지 비교 (날짜만, 시간 무시)
 * @param {Date} a
 * @param {Date} b
 * @returns {boolean}
 */
export const isAfterByDate = (a, b) => {
  const aDate = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const bDate = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return aDate.getTime() > bDate.getTime();
};

/**
 * 특정 월의 일수 반환
 * @param {number} year - 연도
 * @param {number} month - 월 (0-based: 0=1월, 11=12월)
 * @returns {number} 해당 월의 일수
 */
export const getDaysInMonth = (year, month) =>
  new Date(year, month + 1, 0).getDate();

/**
 * 특정 월 1일의 요일 반환
 * @param {number} year - 연도
 * @param {number} month - 월 (0-based)
 * @returns {number} 요일 (0=일요일, 6=토요일)
 */
export const getFirstDayOfMonth = (year, month) =>
  new Date(year, month, 1).getDay();

/**
 * 이전 월의 마지막 날짜 반환
 * @param {number} year - 연도
 * @param {number} month - 월 (0-based)
 * @returns {number} 이전 월의 마지막 일
 */
export const getPrevMonthLastDate = (year, month) =>
  new Date(year, month, 0).getDate();

/**
 * 날짜가 두 날짜 사이에 있는지 확인 (순서 무관)
 * @param {Date} date - 확인할 날짜
 * @param {Date} d1 - 범위 시작
 * @param {Date} d2 - 범위 끝
 * @returns {boolean}
 */
export const isBetweenDates = (date, d1, d2) => {
  if (!d1 || !d2) return false;
  return (date >= d1 && date <= d2) || (date <= d1 && date >= d2);
};

/**
 * 시작일~종료일 사이의 모든 날짜 문자열 배열 생성
 * @param {Date} startDate - 시작일
 * @param {Date} endDate - 종료일
 * @param {string} [format="YYYY-MM-DD"] - 날짜 포맷
 * @returns {string[]} ["2026-05-01", "2026-05-02", ...]
 */
export const getDatesBetween = (startDate, endDate, format = "YYYY-MM-DD") => {
  if (!startDate || !endDate) return [];
  const dates = [];
  let current = new Date(startDate);
  while (current <= endDate) {
    dates.push(formatDateTime(current, format));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

/**
 * 시간 범위에서 일정 간격으로 시간 문자열 배열 생성
 * @param {string} startTime - 시작 시간 "HH:mm"
 * @param {string} endTime - 종료 시간 "HH:mm"
 * @param {number} intervalMin - 간격 (분)
 * @returns {string[]} ["06:00", "06:10", ...]
 */
export const getTimesBetween = (startTime, endTime, intervalMin) => {
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  let cur = new Date(0, 0, 0, sh, sm);
  const end = new Date(0, 0, 0, eh, em);
  const times = [];

  while (cur <= end) {
    const time = `${String(cur.getHours()).padStart(2, "0")}:${String(cur.getMinutes()).padStart(2, "0")}`;
    cur.setMinutes(cur.getMinutes() + intervalMin);
    times.push(time);
  }

  return times;
};

/**
 * 특정 월의 모든 날짜 문자열 배열 생성
 * @param {number} year - 연도
 * @param {number} month - 월 (0-based)
 * @param {string} [format="YYYY-MM-DD"] - 날짜 포맷
 * @returns {string[]} ["2026-05-01", "2026-05-02", ..., "2026-05-31"]
 */
export const getDatesOfMonth = (year, month, format = "YYYY-MM-DD") => {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  return getDatesBetween(start, end, format);
};

/**
 * Date 객체를 지정 포맷의 문자열로 변환
 * @param {Date} date - 변환할 날짜
 * @param {string} [format="YYYY-MM-DD"] - 포맷 (YYYY, MM, DD, HH, mm, ss, DAY)
 * @param {Function} [t=null] - i18n 번역 함수 (요일 번역 시 사용)
 * @returns {string} 포맷된 날짜 문자열
 */
export const formatDateTime = (date, format = "YYYY-MM-DD", t = null) => {
  if (!date || !(date instanceof Date)) return "";

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  const ss = String(date.getSeconds()).padStart(2, "0");

  let dayLabel = "";
  if (t) {
    const dayKeys = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    dayLabel = t(I18N_KEYS.common.days[dayKeys[date.getDay()]]);
  } else {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    dayLabel = days[date.getDay()];
  }

  return format
    .replace("YYYY", y)
    .replace("MM", m)
    .replace("DD", d)
    .replace("HH", hh)
    .replace("mm", mm)
    .replace("ss", ss)
    .replace("DAY", dayLabel);
};

/**
 * 날짜 문자열을 Date 객체로 변환
 * @param {string} dateString - "YYYY-MM-DD" 형식의 날짜 문자열
 * @param {string} [delimiter="-"] - 구분자
 * @returns {Date|null} Date 객체 또는 null
 */
export const parseDate = (dateString, delimiter = "-") => {
  if (!dateString) return null;
  const [y, m, d] = dateString.split(delimiter).map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Date 객체를 커스텀 포맷 문자열로 변환
 * @param {Date} date - 변환할 날짜
 * @param {string} [customString="MM.DD (d)"] - 포맷 (YYYY, MM, DD, d, HH, hh, mm, ss, A)
 * @returns {string|null} 포맷된 문자열 또는 null
 */
export const formatDateToCustomString = (date, customString = "MM.DD (d)") => {
  if (!date) return null;

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const hours = date.getHours();

  const tokens = {
    YYYY: date.getFullYear(),
    MM: String(date.getMonth() + 1).padStart(2, "0"),
    DD: String(date.getDate()).padStart(2, "0"),
    d: days[date.getDay()],
    HH: String(hours).padStart(2, "0"),
    hh: String(hours % 12 || 12).padStart(2, "0"),
    mm: String(date.getMinutes()).padStart(2, "0"),
    ss: String(date.getSeconds()).padStart(2, "0"),
    A: hours < 12 ? "AM" : "PM",
  };

  return customString.replace(
    /YYYY|MM|DD|d|HH|hh|mm|ss|A/g,
    (match) => tokens[match],
  );
};

/**
 * 캘린더 표시의 시작 경계: 해당 월 1일에서 7일 전 날짜
 * @param {Date} date - 기준 날짜
 * @returns {string|undefined} "YYYY-MM-DD" 형식
 */
export const getCalendarFirstDay = (date) => {
  if (!date) return;

  const newDate = new Date(date.getFullYear(), date.getMonth(), 1);
  newDate.setDate(newDate.getDate() - 7);

  const resultYear = newDate.getFullYear();
  const resultMonth = newDate.getMonth() + 1;
  const resultDay = newDate.getDate();
  return `${resultYear}-${String(resultMonth).padStart(2, "0")}-${String(resultDay).padStart(2, "0")}`;
};

/**
 * 캘린더 표시의 종료 경계: 해당 월 마지막 날에서 14일 후 날짜
 * @param {Date} date - 기준 날짜
 * @returns {string|undefined} "YYYY-MM-DD" 형식
 */
export const getCalendarLastDay = (date) => {
  if (!date) return;

  const year = date.getFullYear();
  const month = date.getMonth();
  const lastDayDate = getDaysInMonth(year, month);

  const lastDateOfMonth = new Date(year, month, lastDayDate);
  lastDateOfMonth.setDate(lastDateOfMonth.getDate() + 14);

  const resultYear = lastDateOfMonth.getFullYear();
  const resultMonth = lastDateOfMonth.getMonth() + 1;
  const resultDay = lastDateOfMonth.getDate();

  return `${resultYear}-${String(resultMonth).padStart(2, "0")}-${String(resultDay).padStart(2, "0")}`;
};

/**
 * 특정 월의 첫째 날과 마지막 날 Date 객체 반환
 * @param {Date} date - 기준 날짜
 * @returns {{ firstDay: Date, lastDay: Date }}
 */
export const getMonthRange = (date) => ({
  firstDay: new Date(date.getFullYear(), date.getMonth(), 1),
  lastDay: new Date(date.getFullYear(), date.getMonth() + 1, 0),
});
